import styled from 'styled-components';
import { ResourceTile } from 'components/skeleton/styles';
import { VerticalContentTile } from '@hqo/react-components-library/dist/molecules/tiles/vertical-content-tile';
import { ContentTile } from '@hqo/react-components-library/dist/molecules/tiles/content-tile';
import { DIMENSIONS } from '@hqo/react-components-library/dist/utils';
import { Tag_01 } from '@hqo/react-components-library/dist/icons';
import { ROW_BASIS } from './consts';

export const StyledVerticalContentTile = styled(VerticalContentTile)`
  color: ${({ theme }) => theme.colors.fontColor};
  width: 308px;

  .resource-tile-image {
    width: 308px;
    height: 205px;
  }
`;

export const StyledContentTile = styled(ContentTile)`
  color: ${({ theme }) => theme.colors.fontColor};

  [data-testid='title'] {
    color: ${({ theme }) => theme.colors.fontColor} !important;
    text-decoration: none !important;
  }

  [data-testid='subtitle'] {
    color: ${({ theme }) => theme.colors.palette.systemGreyDark} !important;
    text-decoration: none !important;
  }

  &:hover [data-testid='title'],
  &:focus [data-testid='title'],
  &:focus-within [data-testid='title'] {
    color: ${({ theme }) => theme.colors.fontColor} !important;
    text-decoration: underline !important;
  }

  &:hover [data-testid='subtitle'],
  &:focus [data-testid='subtitle'],
  &:focus-within [data-testid='subtitle'] {
    color: ${({ theme }) => theme.colors.palette.systemGreyDark} !important;
    text-decoration: underline !important;
  }
`;

export const ResponsiveResourceTile = styled(ResourceTile)`
  border-radius: 6px;
  width: 200px;
  height: 128px;
  object-fit: cover;

  @media screen and (max-width: ${DIMENSIONS.MEDIUM}) {
    width: 120px;
    height: 80px;
  }
`;

export const DiscountIconContainer = styled.div`
  width: 20px;
  height: 20px;
  text-align: center;
  border-radius: 6px;
  padding-top: 2px;
  background-color: ${({ theme }) => theme.colors.palette.systemGreenLight};
`;

export const DiscountIcon = styled(Tag_01)`
  width: 16px;
  height: 16px;
  color: ${({ theme }) => theme.colors.palette.systemGreen};
`;

/*
 * The redesigned list row, from the "Coworker Visibility" prototype: a bordered card with a square
 * thumbnail, the name and location stacked beside it, and a chevron closing the row.
 *
 * A row rather than the library's `ContentTile`/`VerticalContentTile` above, which are a photo-first
 * tile — the design puts the plan's resources in a dense list you scan, not a gallery you browse.
 *
 * Split into a plain box (`StyledResourceRowCard`) and the actual `role="button"` row inside it: the
 * favorite star is a real `<button>` and sits as this card's *sibling* to the navigation control, not
 * nested inside it — a `<button>` inside a `div[role=button]` means the star's Enter/Space also
 * bubbles into the row's own `onKeyDown` and triggers navigation instead of (or as well as) favoriting.
 */
export const StyledResourceRowCard = styled.div`
  position: relative;
  padding: 10px;
  /* Room for the absolutely-positioned favorite star in the top-right corner. */
  padding-right: 40px;
  box-sizing: border-box;
  /*
   * A fixed size, whatever shares the line: the card never grows, so one resource is the same tile as
   * twenty. Containers wrap on the basis — as many per line as the width allows — and it only gives
   * way when the container itself is narrower.
   */
  flex: 0 1 ${ROW_BASIS};
  min-width: 0;
  max-width: 100%;

  /*
   * One per line on a phone, where the list is a column: a percentage basis there would be a share of
   * the column's *height*, so a lone card filled the screen. Size by width and let the height be its own.
   */
  @media only screen and (max-width: ${DIMENSIONS.MEDIUM}) {
    flex: none;
    width: 100%;
  }

  border: 1px solid ${({ theme }) => theme.colors.palette.systemGreyMedium};
  border-radius: 14px;
  background: ${({ theme }) => theme.colors.universal.white};
  transition: opacity 120ms;

  /* Hovering the star is hovering the card too — it's visually part of it — so the dim lives here. */
  &:hover {
    opacity: 0.9;
  }
`;

export const StyledResourceRow = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  width: 100%;
  height: 100%;
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 2px;
  }
`;

export const RowThumb = styled.div`
  position: relative;
  width: 64px;
  height: 64px;
  flex-shrink: 0;
  border-radius: 11px;
  overflow: hidden;
  /* Grey stands in for a resource with no photo, so the row keeps its rhythm either way. */
  background: ${({ theme }) => theme.colors.palette.systemGreyLight};
`;

export const RowThumbImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

/* The discount marker the old tile showed over its photo, kept in the thumbnail's corner. */
export const RowDiscountBadge = styled.div`
  position: absolute;
  right: 3px;
  bottom: 3px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.universal.white};
`;

export const RowDiscountIcon = styled(Tag_01)`
  width: 11px;
  height: 11px;
  color: ${({ theme }) => theme.colors.primary};
`;

export const RowText = styled.div`
  flex: 1;
  min-width: 0;
`;

/* The name with the integration badge to its right; the badge keeps its width, the name gives way. */
export const RowNameLine = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  margin-bottom: 3px;

  > :not(:first-child) {
    flex-shrink: 0;
  }
`;

export const RowName = styled.div`
  min-width: 0;
  font: 700 16px/22px ${({ theme }) => theme.fonts.join()};
  color: ${({ theme }) => theme.colors.fontColor};
  /* One line: a long resource name must not push the meta row out of the card. */
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

export const RowSubtitle = styled.div`
  margin-bottom: 7px;
  font: 400 13px/18px ${({ theme }) => theme.fonts.join()};
  color: ${({ theme }) => theme.colors.palette.systemGreyDark};
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

export const RowMeta = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px 12px;
`;

export const RowCapacity = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font: 400 13px/18px ${({ theme }) => theme.fonts.join()};
  color: ${({ theme }) => theme.colors.palette.systemGreyDark};
`;

export const RowChevron = styled.div`
  display: flex;
  flex-shrink: 0;
  color: ${({ theme }) => theme.colors.palette.systemGreyDark};
`;

/* The favorite star sits in the card's top-right corner, clear of the chevron below it. */
export const RowFavorite = styled.div`
  position: absolute;
  top: 2px;
  right: 2px;
`;
